import type {
  CarAngleClassification,
  CarClassification,
  ImageQualityClassification,
  MediaUsageMetadata,
} from "@rumble-it/common-lib/types/models/MediaUsageMetadata.interface";

/**
 * Only intents that represent a specific car angle are listed here. Intents
 * without a meaningful car angle (e.g. documents) are intentionally absent.
 *
 * Backend intents: project://backend/src/Enum/MediaUsageIntent.php
 * Backend angles:  project://backend/src/Domain/Media/Enum/CarAngleClassification.php
 */
const INTENT_TO_EXPECTED_CAR_ANGLE: Partial<
  Record<string, CarAngleClassification>
> = {
  //not-implemented: "front-centre",
  frontleft: "front-left",
  frontright: "front-right",
  //not-implemented: "left",
  //not-implemented: "right",
  //not-implemented: "rear-centre",
  rearleft: "rear-left",
  rearright: "rear-right",
  //not-implemented: "roof",
};

/**
 * Human-readable (Dutch) labels for the car angle classifications.
 */
export const CAR_ANGLE_CLASSIFICATION_LABELS: Record<
  CarAngleClassification,
  string
> = {
  "front-centre": "Voorkant (midden)",
  "front-left": "Voorkant (links)",
  "front-right": "Voorkant (rechts)",
  left: "Linkerzijde",
  right: "Rechterzijde",
  "rear-centre": "Achterkant (midden)",
  "rear-left": "Achterkant (links)",
  "rear-right": "Achterkant (rechts)",
  roof: "Dak",
  unknown: "Onbekend",
};

export const getCarAngleClassificationLabel = (
  angle: CarAngleClassification,
): string => CAR_ANGLE_CLASSIFICATION_LABELS[angle] ?? angle;

export type CarClassificationEvaluation =
  | { status: "not-evaluated" }
  | {
      status: "approved" | "rejected";
      approvals: string[];
      problems: string[];
    };

/**
 * Result of evaluating a single classification: either approved (with a
 * human-readable description) or a problem (with a human-readable description).
 */
type ClassificationResult =
  | { ok: true; description: string }
  | { ok: false; problem: string };

/**
 * Total number of independent AI classifications performed per image
 * (is-it-a-car, car angle and image quality).
 */
export const TOTAL_CLASSIFICATIONS = 3;

/**
 * Returns how many of the AI classifications have been completed for the given
 * metadata, from 0 up to TOTAL_CLASSIFICATIONS.
 *
 * Each classification is dispatched as a separate asynchronous command, so the
 * individual fields are populated independently over time.
 */
export const getClassificationAmount = (
  metadata: MediaUsageMetadata | null | undefined,
): number => {
  if (!metadata) {
    return 0;
  }

  return [
    metadata.carClassification,
    metadata.carAngleClassification,
    metadata.imageQualityClassification,
  ].filter((classification) => classification != null).length;
};

/**
 * Evaluates the "is it a car?" classification.
 */
const evaluateIsCar = (
  carClassification: CarClassification | null | undefined,
): ClassificationResult => {
  switch (carClassification) {
    case "car":
      return {
        ok: true,
        description: "RumbleAI heeft een auto herkend op de afbeelding",
      };
    case "not-car":
    case null:
    case undefined:
      return {
        ok: false,
        problem: "RumbleAI heeft geen auto herkend op de afbeelding",
      };
    default: {
      // Exhaustiveness guard: a new CarClassification value must be handled.
      const _exhaustive: never = carClassification;
      return {
        ok: false,
        problem: `RumbleAI gaf een onbekende auto-classificatie terug (${String(
          _exhaustive,
        )})`,
      };
    }
  }
};

/**
 * Evaluates the car-angle classification against the angle expected for the
 * image's intent.
 */
const evaluateCarAngle = (
  carAngleClassification: CarAngleClassification | null | undefined,
  expected: CarAngleClassification,
): ClassificationResult => {
  if (carAngleClassification == null) {
    return {
      ok: false,
      problem:
        "RumbleAI heeft geen positieherkenning kunnen uitvoeren op de afbeelding",
    };
  }

  if (carAngleClassification !== expected) {
    return {
      ok: false,
      problem: `RumbleAI heeft foutieve positie herkend (verwacht: ${getCarAngleClassificationLabel(
        expected,
      )}, herkend: ${getCarAngleClassificationLabel(carAngleClassification)})`,
    };
  }

  return {
    ok: true,
    description: `RumbleAI heeft de juiste positie herkend (${getCarAngleClassificationLabel(
      expected,
    )})`,
  };
};

/**
 * Evaluates the image-quality classification.
 */
const evaluateImageQuality = (
  imageQualityClassification: ImageQualityClassification | null | undefined,
): ClassificationResult => {
  switch (imageQualityClassification) {
    case "high-quality":
      return {
        ok: true,
        description:
          "RumbleAI heeft een goede beeldkwaliteit geclassificeerd voor deze afbeelding",
      };
    case "low-quality":
      return {
        ok: false,
        problem:
          "RumbleAI heeft lage beeldkwaliteit geclassificeerd voor deze afbeelding",
      };
    case null:
    case undefined:
      return {
        ok: false,
        problem: "RumbleAI heeft de beeldkwaliteit niet kunnen bepalen",
      };
    default: {
      // Exhaustiveness guard: a new ImageQualityClassification value must be
      // handled.
      const _exhaustive: never = imageQualityClassification;
      return {
        ok: false,
        problem: `RumbleAI gaf een onbekende beeldkwaliteit terug (${String(
          _exhaustive,
        )})`,
      };
    }
  }
};

export const evaluateCarClassification = (
  intent: string | undefined,
  metadata: MediaUsageMetadata | null | undefined,
): CarClassificationEvaluation => {
  const expected = intent ? INTENT_TO_EXPECTED_CAR_ANGLE[intent] : undefined;

  // Only car-angle intents are subject to evaluation.
  if (!expected) {
    return { status: "not-evaluated" };
  }

  // Nothing to show until at least one classification has completed. Each
  // classification runs as a separate async command and is populated
  // independently over time.
  if (!metadata || getClassificationAmount(metadata) === 0) {
    return { status: "not-evaluated" };
  }

  // Evaluate progressively: only the classifications that have completed so
  // far are taken into account. Pending (null) classifications are skipped so
  // they are not reported as problems while still in flight.
  const results: ClassificationResult[] = [];

  if (metadata.carClassification != null) {
    results.push(evaluateIsCar(metadata.carClassification));
  }

  if (metadata.carAngleClassification != null) {
    results.push(evaluateCarAngle(metadata.carAngleClassification, expected));
  }

  if (metadata.imageQualityClassification != null) {
    results.push(evaluateImageQuality(metadata.imageQualityClassification));
  }

  const approvals = results
    .filter(
      (result): result is Extract<ClassificationResult, { ok: true }> =>
        result.ok,
    )
    .map((result) => result.description);
  const problems = results
    .filter(
      (result): result is Extract<ClassificationResult, { ok: false }> =>
        !result.ok,
    )
    .map((result) => result.problem);

  return {
    status: problems.length > 0 ? "rejected" : "approved",
    approvals,
    problems,
  };
};
